// lib/supabase.js
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseKey)

// Database service functions
export const dbService = {
  // Clients
  async getClients() {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .order('created_at', { ascending: false })
    return { data, error }
  },

  async createClient(client) {
    const { data, error } = await supabase
      .from('clients')
      .insert([client])
      .select()
    return { data, error }
  },

  async updateClient(id, updates) {
    const { data, error } = await supabase
      .from('clients')
      .update(updates)
      .eq('id', id)
      .select()
    return { data, error }
  },

  async deleteClient(id) {
    const { data, error } = await supabase
      .from('clients')
      .delete()
      .eq('id', id)
    return { data, error }
  },

  // Capsules
  async getCapsules(clientId = null) {
    let query = supabase
      .from('capsules')
      .select(`
        *,
        clients(name),
        items(
          id,
          name,
          photo_url,
          brand,
          category
        ),
        outfits(
          id,
          name,
          description,
          outfit_items(
            item_id,
            items(id, name, photo_url)
          )
        )
      `)
      .order('created_at', { ascending: false })
    
    if (clientId) {
      query = query.eq('client_id', clientId)
    }
    
    const { data, error } = await query
    return { data, error }
  },

  async getCapsule(id) {
    const { data, error } = await supabase
      .from('capsules')
      .select(`
        *,
        clients(id, name, email),
        items(*),
        outfits(
          *,
          outfit_items(
            order_index,
            items(*)
          )
        )
      `)
      .eq('id', id)
      .single()
    
    // Debug logging
    console.log('getCapsule result:', { data, error, id });
    
    return { data, error }
  },

  async createCapsule(capsule) {
    const { data, error } = await supabase
      .from('capsules')
      .insert([capsule])
      .select()
    return { data, error }
  },

  async updateCapsule(id, updates) {
    const { data, error } = await supabase
      .from('capsules')
      .update(updates)
      .eq('id', id)
      .select()
    return { data, error }
  },

  async deleteCapsule(id) {
    const { data, error } = await supabase
      .from('capsules')
      .delete()
      .eq('id', id)
    return { data, error }
  },

  // Outfits
  async getOutfits(capsuleId) {
    const { data, error } = await supabase
      .from('outfits')
      .select(`
        *,
        outfit_items(
          order_index,
          items(*)
        )
      `)
      .eq('capsule_id', capsuleId)
      .order('order_index', { ascending: true })
    return { data, error }
  },

  async getOutfit(id) {
    const { data, error } = await supabase
      .from('outfits')
      .select(`
        *,
        outfit_items(
          order_index,
          items(*)
        )
      `)
      .eq('id', id)
      .single()
    
    if (data && data.outfit_items) {
      // Transform the data to have items array sorted by order_index
      data.items = data.outfit_items
        .sort((a, b) => a.order_index - b.order_index)
        .map(oi => oi.items)
    }
    
    return { data, error }
  },

  async createOutfit(outfit) {
    const { data, error } = await supabase
      .from('outfits')
      .insert([outfit])
      .select()
    return { data, error }
  },

  async updateOutfit(id, updates) {
    const { data, error } = await supabase
      .from('outfits')
      .update(updates)
      .eq('id', id)
      .select()
    return { data, error }
  },

  async deleteOutfit(id) {
    const { data, error } = await supabase
      .from('outfits')
      .delete()
      .eq('id', id)
    return { data, error }
  },

  // Items (Capsule-specific)
  async getItemsForCapsule(capsuleId) {
    const { data, error } = await supabase
      .from('items')
      .select('*')
      .eq('capsule_id', capsuleId)
      .order('created_at', { ascending: false })
    
    console.log('getItemsForCapsule result:', { data, error, capsuleId });
    return { data, error }
  },

  async getItemsForClient(clientId) {
    // Get items from all capsules belonging to this client
    const { data, error } = await supabase
      .from('items')
      .select(`
        *,
        capsules!inner(client_id)
      `)
      .eq('capsules.client_id', clientId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  async createItem(item) {
    const { data, error } = await supabase
      .from('items')
      .insert([item])
      .select()
    return { data, error }
  },

  async updateItem(id, updates) {
    const { data, error } = await supabase
      .from('items')
      .update(updates)
      .eq('id', id)
      .select()
    return { data, error }
  },

  async deleteItem(id) {
    const { data, error } = await supabase
      .from('items')
      .delete()
      .eq('id', id)
    return { data, error }
  },

  // Outfit-Item Relationships
  async addItemToOutfit(outfitId, itemId) {
    // Get current max order_index for this outfit
    const { data: existing } = await supabase
      .from('outfit_items')
      .select('order_index')
      .eq('outfit_id', outfitId)
      .order('order_index', { ascending: false })
      .limit(1)
    
    const nextOrderIndex = existing && existing.length > 0 ? existing[0].order_index + 1 : 0
    
    const { data, error } = await supabase
      .from('outfit_items')
      .insert([{
        outfit_id: outfitId,
        item_id: itemId,
        order_index: nextOrderIndex
      }])
      .select()
    
    return { data, error }
  },

  async removeItemFromOutfit(outfitId, itemId) {
    const { data, error } = await supabase
      .from('outfit_items')
      .delete()
      .eq('outfit_id', outfitId)
      .eq('item_id', itemId)
    
    return { data, error }
  },

  async updateOutfitItemOrder(outfitId, orderUpdates) {
    // orderUpdates is an array of { item_id, order_index }
    const promises = orderUpdates.map(update => 
      supabase
        .from('outfit_items')
        .update({ order_index: update.order_index })
        .eq('outfit_id', outfitId)
        .eq('item_id', update.item_id)
    )
    
    const results = await Promise.all(promises)
    const hasError = results.some(result => result.error)
    
    return { 
      data: hasError ? null : results, 
      error: hasError ? results.find(r => r.error).error : null 
    }
  },

  async getOutfitItems(outfitId) {
    const { data, error } = await supabase
      .from('outfit_items')
      .select(`
        *,
        items(*)
      `)
      .eq('outfit_id', outfitId)
      .order('order_index', { ascending: true })
    
    return { data, error }
  },

  // Legacy support - kept for backward compatibility
  async getItems(outfitId) {
    // This now gets items through the outfit_items junction table
    return this.getOutfitItems(outfitId)
  },

  // File upload
  async uploadItemPhoto(file, itemId) {
    const fileExt = file.name.split('.').pop()
    const fileName = `${itemId}-${Date.now()}.${fileExt}`
    
    const { data, error } = await supabase.storage
      .from('item-photos')
      .upload(fileName, file)
    
    if (error) return { data: null, error }
    
    const { data: urlData } = supabase.storage
      .from('item-photos')
      .getPublicUrl(fileName)
    
    return { data: urlData.publicUrl, error: null }
  },

  async deleteItemPhoto(url) {
    const fileName = url.split('/').pop()
    const { error } = await supabase.storage
      .from('item-photos')
      .remove([fileName])
    return { error }
  },



async getCheatSheets(capsuleId) {
  const { data, error } = await supabase
    .from('cheat_sheets')
    .select(`
      *,
      cheat_sheet_items:cheat_sheet_items(
        id,
        item_id,
        order_index,
        items:items(
          id,
          name,
          photo_url,
          brand
        )
      )
    `)
    .eq('capsule_id', capsuleId)
    .order('created_at', { ascending: false });
  
  return { data, error };
},

async createCheatSheet(cheatSheetData) {
  const { data, error } = await supabase
    .from('cheat_sheets')
    .insert([cheatSheetData])
    .select()
    .single();
  
  return { data, error };
},

async updateCheatSheet(id, cheatSheetData) {
  const { data, error } = await supabase
    .from('cheat_sheets')
    .update({
      ...cheatSheetData,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();
  
  return { data, error };
},

async deleteCheatSheet(id) {
  const { data, error } = await supabase
    .from('cheat_sheets')
    .delete()
    .eq('id', id);
  
  return { data, error };
},

async getCheatSheetItems(cheatSheetId) {
  const { data, error } = await supabase
    .from('cheat_sheet_items')
    .select(`
      *,
      items:items(*)
    `)
    .eq('cheat_sheet_id', cheatSheetId)
    .order('order_index', { ascending: true });
  
  return { data, error };
},

async updateCheatSheetItems(cheatSheetId, itemIds) {
  // First, delete existing associations
  await supabase
    .from('cheat_sheet_items')
    .delete()
    .eq('cheat_sheet_id', cheatSheetId);
  
  // Then, insert new associations
  if (itemIds.length > 0) {
    const associations = itemIds.map((itemId, index) => ({
      cheat_sheet_id: cheatSheetId,
      item_id: itemId,
      order_index: index
    }));
    
    const { data, error } = await supabase
      .from('cheat_sheet_items')
      .insert(associations)
      .select();
    
    return { data, error };
  }
  
  return { data: [], error: null };
},

async uploadCheatSheetImage(file, cheatSheetId) {
  const fileExt = file.name.split('.').pop();
  const fileName = `cheat-sheet-${cheatSheetId}-${Date.now()}.${fileExt}`;
  const filePath = `${fileName}`;

  const { error: uploadError } = await supabase.storage
    .from('cheat-sheets')
    .upload(filePath, file);

  if (uploadError) {
    return { data: null, error: uploadError };
  }

  const { data: { publicUrl } } = supabase.storage
    .from('cheat-sheets')
    .getPublicUrl(filePath);

  return { data: publicUrl, error: null };
},

// Add these methods to your dbService for public cheat sheet access

// Get cheat sheets for public viewing (no auth required)
async getPublicCheatSheets(capsuleId) {
  const { data, error } = await supabase
    .from('cheat_sheets')
    .select(`
      *,
      cheat_sheet_items:cheat_sheet_items(
        id,
        item_id,
        order_index,
        items:items(
          id,
          name,
          photo_url,
          brand,
          category,
          color,
          size,
          price,
          description,
          link
        )
      )
    `)
    .eq('capsule_id', capsuleId)
    .order('created_at', { ascending: false });
  
  return { data, error };
},

// Get items in a specific cheat sheet for public viewing
async getPublicCheatSheetItems(cheatSheetId) {
  const { data, error } = await supabase
    .from('cheat_sheet_items')
    .select(`
      *,
      items:items(
        id,
        name,
        photo_url,
        brand,
        category,
        color,
        size,
        price,
        description,
        link
      )
    `)
    .eq('cheat_sheet_id', cheatSheetId)
    .order('order_index', { ascending: true });
  
  return { data, error };
}
};

